import { StringMap } from 'components/views/company/outreach-template/message-bank.context';
import { nextId } from './next.id';
import { Action, ActionType, Day, DelayAction } from './types';

export const getMinIdMinusOne = (arr: { id: string }[]) => {
  const lessThanZero = arr.filter(({ id }) => Number(id) < 0);
  return (
    lessThanZero.length > 0 ? Math.min(...lessThanZero.map(({ id }) => Number(id))) - 1 : -1
  ).toString();
};

export const mapToLegacy = (days: Day[]) => {
  const result = {};
  let dayIndex = 1;
  const messageMap = new Map<string, string>();
  for (const actions of days) {
    const day = {};
    actions.actions.forEach((action, idx) => {
      if (action.action == ActionType.MMS) {
        const hasSavedMsgId = action.msg_id != undefined && Number(action.msg_id) > 0;
        if (hasSavedMsgId) {
          day[(idx + 1).toString()] = {
            action: action.action,
            time: action.time,
            msg_id: action.msg_id,
            message: action.message,
          };
        } else {
          day[(idx + 1).toString()] = {
            action: action.action,
            time: action.time,
            message: action.message,
            url: action.url,
          };
        }
        return;
      }

      let message: string | undefined = undefined;
      let msg_id: string | undefined = undefined;

      if (action.action == ActionType.SMS && action.msg_id != undefined) {
        if (!messageMap.has(action.msg_id)) {
          if (action.message) {
            messageMap.set(action.msg_id, action.message);
            message = action.message;
          }
        } else if (Number(action.msg_id) < 0) {
          message = messageMap.get(action.msg_id);
        }

        msg_id = Number(action.msg_id ?? 0) <= 0 ? undefined : action.msg_id;
      }
      return (day[(idx + 1).toString()] = {
        ...action,
        id: undefined,
        msg_id,
        message,
      });
    });
    result[`day${dayIndex++}`] = day;
  }
  return JSON.stringify(result, null, 2);
};

export const mapFromLegacy = (json: string | undefined): Day[] => {
  if (!json) {
    return [];
  }
  const parsed = JSON.parse(json);
  // Valid JSON but no `flow` key (e.g. a "{}" config or a migration edge case)
  // would make Object.entries throw — treat it as an empty template.
  if (!parsed?.flow) {
    return [];
  }
  return Object.entries(parsed.flow).map(([, day]) => {
    const actions = Object.entries(day as any).map(([, action]) => {
      const a = action as Action;
      a.id = nextId();
      return a;
    });
    return { id: nextId(), actions };
  });
};

export const OVERWRITTEN_ITEM_TEXT = '(overwritten)';
export const CUSTOM_ITEM_TEXT = '(custom)';

export const mergeReplacements = (
  replacements: StringMap,
  customReplacements: string[][],
): [StringMap, [string, string][]] => {
  const result: StringMap = { ...replacements };
  for (const [key, value] of customReplacements ?? []) {
    if (key in result) {
      if (value != result[key]) {
        result[key] = `${value} ${OVERWRITTEN_ITEM_TEXT}`;
      }
    } else {
      result[key] = `${value} ${CUSTOM_ITEM_TEXT}`;
    }
  }
  return [{ ...replacements, ...Object.fromEntries(customReplacements) }, Object.entries(result)];
};

export const replaceCallsWithDelay = (days: Day[]) => {
  for (const actions of days) {
    actions.actions = actions.actions.map((action) => {
      if (action.action == ActionType.CALL) {
        return <DelayAction>{
          id: action.id,
          action: ActionType.DELAY,
          time: action.time,
        };
      }
      return action;
    });
  }
  return days;
};

export const removeCalls = (days: Day[]) => {
  for (const actions of days) {
    const newActions: Action[] = [];
    for (let i = 0; i < actions.actions.length; i++) {
      const action = actions.actions[i];
      if (action.action == ActionType.CALL) {
        const nextAction = actions.actions[i + 1];
        if (
          (nextAction?.action == ActionType.SMS || nextAction?.action == ActionType.MMS) &&
          nextAction.time.startsWith('delay:')
        ) {
          newActions.push({
            ...nextAction,
            time: action.time,
          });
          i++;
        }
      } else {
        newActions.push(action);
      }
    }
    actions.actions = newActions;
  }
  return days;
};

const replaceableTimes = new Set([
  'morning',
  'morning-early',
  'morning-prime',
  'afternoon',
  'evening-prime',
  'evening',
  'after-work',
  'delay:10min',
  'delay:1-5min',
  'delay:1min',
  'delay:2-4min',
  'delay:2hours',
  'delay:4hours',
  'delay:5-15min',
]);
export const replaceDelaysWithSms = (days: Day[], msgId: string, isNewMessage: boolean) => {
  for (const actions of days) {
    const newActions: Action[] = [];
    let addedForToday = false;
    for (let i = 0; i < actions.actions.length; i++) {
      const action = actions.actions[i];
      const nextAction = actions.actions[i + 1];

      if (!addedForToday && action.action == ActionType.DELAY) {
        if (nextAction?.action == ActionType.SMS || nextAction?.action == ActionType.MMS) {
          // if we're scheduled to send an SMS/MMS after this, then this wouldn't be a follow-up
          if (nextAction.time.startsWith('delay:')) {
            newActions.push(action);
            continue;
          }
        }
        const { time } = action;
        if (
          i > 0 && // should never be the first action
          replaceableTimes.has(time)
        ) {
          addedForToday = true; // only one follow-up per day
          newActions.push({
            id: action.id,
            action: ActionType.SMS,
            time: time,
            msg: undefined,
            msg_id: msgId,
            message: isNewMessage ? '_REQUEST_CALL_' : undefined,
          });
        } else {
          newActions.push(action);
        }
      } else {
        newActions.push(action);
      }
    }
    actions.actions = newActions;
  }
  return days;
};
